import React, { useState } from "react";

import {
  DetailDisplay,
  Dialog,
  ErrorButton,
  ErrorMessage,
  PrimaryButton,
  SecondaryButton,
  Table,
  Text,
} from "widgets";

import {
  ADJUSTMENT_AGAINST_REFUNDS,
  BUSINESS_VERTICALS,
  CASH_PAYMENT_METHOD,
  DEFAULT_COUNTRY_NAME,
  INR_SYMBOL,
  IS_JODO_DISABLED,
  KH_VERTICAL_ID,
  LEARN_VERTICAL_ID,
  LOAN_PAYMENT_METHOD,
  MANDATE_PAYMENT_METHOD,
  ORDER_STATUSES,
  PACKAGE_DETAILS_COLUMN,
  PART_PAYMENT_DETAILS_COLUMN,
  PAYMENT_DETAILS_COLUMN,
  PAYMENT_GATEWAY_PROVIDERS_MAP,
  PAYMENT_STATUSES,
  PAYMENTS_ATTEMPTS_COLUMN,
  PENDING_ORDER_STATUS,
  REFUND_DETAILS_COLUMN,
  RECEIPT_DOWNLOAD_DISABLED_AFTER,
  COMPONENT_CATEGORIES,
} from "constants/config";
import DiscardOrder from "components/DiscardOrder/DiscardOrder";
import CancelOrder from "components/CancelOrder/CancelOrder";
import {
  useFetchOrderSummaryPDF,
  useFetchPartPaymentInstallmentTaxInvoice,
  useFetchPaymentReceipt,
  useFetchTaxInvoicePDF,
} from "store/backend/assets";
import Loader from "Shared/Loader/Loader";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import { useNavigate, useSearchParams } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import EventNoteIcon from "@mui/icons-material/EventNote";
import SmartBatching from "components/CourseEnroll/CourseEnroll";
import DownloadInvoice from "components/DownloadInvoice/DownloadInvoice";

import styles from "./CustomerOrderDetails.module.scss";

const getFormattedPackageDetails = (orderDetail) => {
  if (!orderDetail || !Array.isArray(orderDetail.packages)) {
    return [];
  }
  return orderDetail.packages.flatMap((pkg) => {
    const subscriptions = pkg.subscriptions || [];
    return subscriptions.map((sub) => {
      const grade =
        pkg.package_grades?.map((grade) => grade.grade_name).join(", ") ||
        "N/A";
      return {
        name: pkg.display_name || "N/A",
        price: INR_SYMBOL + (sub.mrp || 0).toLocaleString("en-IN"),
        discount:
          INR_SYMBOL +
          (sub.discount || [])
            .reduce((total, d) => total + d.amount, 0)
            .toLocaleString("en-IN"),
        duration: sub.no_of_days || 0,
        grade: grade,
        hasPaymentHead:
          Array.isArray(sub.payment_heads) && sub.payment_heads.length > 0,
      };
    });
  });
};

const getFormattedRefundDetails = (refundDetails) => {
  if (!refundDetails || !Array.isArray(refundDetails)) {
    return [];
  }
  return refundDetails.map((refundDetail) => {
    return {
      amount: refundDetail.inr_conversion.toLocaleString("en-IN"),
      status: refundDetail.status?.name,
      refundDate: new Date(refundDetail.refund_date).toLocaleString(),
      refundReason: refundDetail.reason.name,
    };
  });
};


const getFormattedPartPaymentDetails = (
  partPaymentDetails,
) => {
  if (!partPaymentDetails || !Array.isArray(partPaymentDetails)) {
    return [];
  }

  return partPaymentDetails.flatMap((payment) =>
    payment.installments
      .slice()
      .sort((a, b) => a.installment_no - b.installment_no)
      .map((installment) => {
        return {
          installmentNo: installment.installment_no,
          amount: installment.amount.toLocaleString("en-IN"),
          amountPaid: installment.amount_paid.toLocaleString("en-IN"),
          status: installment.status.name,
          dueDate: installment.due_date,
        };
      })
  );
};

const handleOnClickDownloadPaymentReceipt = (
  paymentId,
  fetchPaymentReceipt,
  openSnackbar
) => {
  if (paymentId) {
    fetchPaymentReceipt(paymentId)
      .then((response) => {
        const link = document.createElement("a");
        link.href = response.signedUrl;
        link.click();
        openSnackbar("Payment receipt downloaded successfully!", "success");
      })
      .catch((error) => {
        console.error("Error fetching receipt", error);
        openSnackbar(
          error.message || "Error fetching payment receipt",
          "error"
        );
      });
  } else {
    console.error("No payment ID provided");
  }
};

const handleDialogClose = (setOpenDialogContent) => () => {
  setOpenDialogContent(null);
};

const handleDialogOpen =
  (setOpenDialogContent, content, size = {}) =>
  () => {
    setOpenDialogContent({ content, size });
  };
const getPaymentStatusName = (status, paymentMethod) => {
  if (status.id === PAYMENT_STATUSES.DISBURSED && paymentMethod === CASH_PAYMENT_METHOD) {
    return "ACKNOWLEDGED";
  }
  return status.name;
}
const getFormattedPaymentDetails = (
  selectedOrder,
  fetchPaymentReceipt,
  isFetchPaymentReceiptLoading,
  openSnackbar,
  setOpenDialogContent
) => {
  if (!selectedOrder || !Array.isArray(selectedOrder.payment_details))
    return [];

  const orderCreatedDate = new Date(selectedOrder.order_created_date);
  const isReceiptDownloadDisabledForOrder =
  selectedOrder.business_unit_id === BUSINESS_VERTICALS.KH &&
  orderCreatedDate > RECEIPT_DOWNLOAD_DISABLED_AFTER;

  const hasProviderColumn = selectedOrder.payment_details.some(
    (p) => p.transaction_details && p.transaction_details.provider_id != null
  );

  return selectedOrder.payment_details
    .sort((p1, p2) => new Date(p2.date) - new Date(p1.date))
    .map((paymentDetail) => {
      const paymentStatus = paymentDetail.status.id;
      const paymentMethod = paymentDetail.method.id;

      const canDownloadReceipt = () =>
        !isReceiptDownloadDisabledForOrder &&
        [
          PAYMENT_STATUSES.APPROVED_BY_PROVIDER,
          PAYMENT_STATUSES.DISBURSED,
        ].includes(paymentStatus) &&
        paymentMethod !== LOAN_PAYMENT_METHOD &&
        paymentMethod !== ADJUSTMENT_AGAINST_REFUNDS;

      const canDownloadTaxInvoice = () =>
        [
          PAYMENT_STATUSES.APPROVED_BY_PROVIDER,
          PAYMENT_STATUSES.DISBURSED,
        ].includes(paymentStatus) &&
        paymentMethod !== ADJUSTMENT_AGAINST_REFUNDS;

      const providerId =
        paymentDetail.transaction_details &&
        paymentDetail.transaction_details.provider_id;

      const providerName =
        paymentMethod === LOAN_PAYMENT_METHOD
          ? null
          : providerId
            ? PAYMENT_GATEWAY_PROVIDERS_MAP[providerId] || providerId
            : paymentMethod === MANDATE_PAYMENT_METHOD
              ? "Jodo Flex"
              : null;

      return {
        amount: INR_SYMBOL + paymentDetail.amount.toLocaleString("en-IN"),
        status: getPaymentStatusName(paymentDetail.status, paymentMethod),
        paymentDate: new Date(paymentDetail.date).toLocaleString(),
        paymentMethod: paymentDetail.method.name,
        ...(hasProviderColumn && {
          paymentProvider: providerName ?? "N/A",
        }),
        receipt: canDownloadReceipt() ? (
          <PrimaryButton
            onClick={() =>
              handleOnClickDownloadPaymentReceipt(
                paymentDetail.id,
                fetchPaymentReceipt,
                openSnackbar
              )
            }
            disabled={isFetchPaymentReceiptLoading}
          >
            {isFetchPaymentReceiptLoading ? <Loader /> : "Download"}
          </PrimaryButton>
        ) : (
          "N/A"
        ),
        taxInvoice: canDownloadTaxInvoice() ? (
          <PrimaryButton
            onClick={handleDialogOpen(
              setOpenDialogContent,
              <DownloadInvoice
                onClose={handleDialogClose(setOpenDialogContent)}
                paymentId={paymentDetail.id}
              />,
              { width: "50rem", height: "20rem" }
            )}
          >
            Download
          </PrimaryButton>
        ) : (
          "N/A"
        ),
        history: Array.isArray(
          paymentDetail.transaction_details?.payment_attempts
        )
          ? paymentDetail.transaction_details.payment_attempts.map(
              (attempt) => ({
                date: new Date(attempt.date).toLocaleString(),
                method: attempt.method?.name || "N/A",
                status: attempt.status?.name || "N/A",
                reason: attempt.reason?.name || "N/A",
                description: attempt.error?.description ? (
                  <Tooltip title={attempt.error.description} arrow>
                    <span>
                      {attempt.error.description.length > 50
                        ? `${attempt.error.description.slice(0, 50)}...`
                        : attempt.error.description}
                    </span>
                  </Tooltip>
                ) : (
                  "N/A"
                ),
              })
            )
          : [],
      };
    });
};

const handleClickGetOrderSummaryPDFButton = (
  orderId,
  refetchOrderSummaryPDF,
  openSnackbar
) => {
  if (!!orderId) {
    refetchOrderSummaryPDF()
      .then((response) => {
        if (response && response.status === "success") {
          const order_summary_response = response.data;
          if (!!order_summary_response.message) {
            openSnackbar(order_summary_response.message);
          }
          if (!!order_summary_response.signed_url) {
            const link = document.createElement("a");
            link.href = order_summary_response.signed_url;
            link.download = `order_summary_${orderId}.pdf`;
            link.click();
            openSnackbar("Order summary downloaded successfully!", "success");
          }
        } else {
          openSnackbar(
            response.error.message || "Error fetching Order Summary"
          );
        }
      })
      .catch((error) => {
        openSnackbar("Error fetching Order Summary");
      });
  }
};

const handleClickGetTaxInvoiceButton = (
  orderId,
  fetchTaxInvoicePDF,
  openSnackbar
) => {
  if (orderId) {
    fetchTaxInvoicePDF(orderId)
      .then((response) => {
        const link = document.createElement("a");
        link.href = response.signedUrl;
        link.click();
        openSnackbar("Tax Invoice downloaded successfully!", "success");
      })
      .catch((error) => {
        console.error("Error fetching tax invoice", error);
        openSnackbar(error.message || "Error fetching tax invoice", "error");
      });
  } else {
    console.error("No order ID provided");
  }
};

const handleClickProceedToPaymentButton = (selectedOrderID) => {
  const paymentPageUrl = `/paymentPreference?order_id=${selectedOrderID}`;
  window.location.href = paymentPageUrl;
  return;
};

const handleClickTransferOrder = (
  navigate,
  uamId,
  selectedOrder,
  selectedBusinessVerticalId
) => {
  navigate(
    `/punchOrder?uamId=${uamId}&verticalId=${selectedBusinessVerticalId}`,
    {
      state: { transferOrderDetails: selectedOrder },
    }
  );
};

const canDiscardOrder = (selectedOrder) => selectedOrder.amount_paid === 0;

const canRequestOrderCancellation = (selectedOrder) => {
  if (selectedOrder.amount_paid === 0) {
    return false;
  }
  return true;
};

const handleClickEditPaymentsButton = (selectedOrder) => {
  const newUrl = `/editPayments?order_id=${selectedOrder.order_id}`;
  window.location.href = newUrl;
  return;
};

const canRequestTransferOrder = (orderDetails, selectedOrder) => {
  const nonSelectedPendingOrders = orderDetails.filter(
    (order) =>
      order.order_id !== selectedOrder.order_id &&
      PENDING_ORDER_STATUS.includes(order.status.id)
  );

  if (
    nonSelectedPendingOrders.length === 0 &&
    selectedOrder.status.id === ORDER_STATUSES.FULFILLED
  ) {
    return true;
  }

  if (
    selectedOrder.status.id === ORDER_STATUSES.ACCEPTED &&
    selectedOrder.amount_paid > 0
  ) {
    return true;
  }

  if (selectedOrder.status.id === ORDER_STATUSES.FULFILLED) {
    return true;
  }
  return false;
};

const hasPaymentAttemptDetails = (paymentDetail) => {
  return (
    paymentDetail.transaction_details &&
    Array.isArray(paymentDetail.transaction_details.payment_attempts) &&
    paymentDetail.transaction_details.payment_attempts.length > 0
  );
};
const handleOnClickEditPaymentsButton = (
  orderId,
  navigate,
  selectedBusinessVerticalId
) => {
  navigate(
    `/editPaymentFlowType?orderId=${orderId}&verticalId=${selectedBusinessVerticalId}`
  );
};

const hasPaymentDetailsProviderColumn = (paymentDetails) =>
  paymentDetails.some(
    (row) =>
      row.transaction_details && row.transaction_details.provider_id != null
  );

export const shouldSmartBatchingComponent = (order) => {
  if (!order) return false;

  if (order.business_unit_id === KH_VERTICAL_ID) return false;

  return order.packages?.some((pkg) =>
    pkg.is_smart_batching_enabled === true
  );
};

const CustomerOrderDetails = (props) => {
  const {
    orderId,
    orderDetails,
    selectedOrder,
    setSelectedOrderID,
    setSelectedOrder,
    orderStatus,
    uamId,
  } = props;
  const [searchParams] = useSearchParams();
  const { openSnackbar } = useSnackbarContext();
  const [openDialogContent, setOpenDialogContent] = useState(null);
  const customerUAMId = searchParams.get("uamId");
  const selectedBusinessVerticalId = searchParams.get("verticalId");
  const navigate = useNavigate();

  const {
    isLoading: isOrderSummaryPDFLoading,
    refetch: refetchOrderSummaryPDF,
  } = useFetchOrderSummaryPDF(orderId);

  const { fetchTaxInvoicePDF, isLoading: isTaxInvoicePDFLoading } =
    useFetchTaxInvoicePDF();

  const { fetchPaymentReceipt, isLoading: isFetchPaymentReceiptLoading } =
    useFetchPaymentReceipt();

  const {
    fetchPartPaymentInstallmentTaxInvoice,
    isLoading: isFetchInstallmentTaxInvoiceLoading,
  } = useFetchPartPaymentInstallmentTaxInvoice();

  const packageDetails = selectedOrder
    ? getFormattedPackageDetails(selectedOrder)
    : [];
  const hasProviderColumn = hasPaymentDetailsProviderColumn(
    selectedOrder ? selectedOrder.payment_details : []
  );
  const getPaymentDetailsColumns = () => {
    const updatedColumns = [...PAYMENT_DETAILS_COLUMN];
    const paymentProviderColumn = {
      label: "Payment Provider",
      field: "paymentProvider",
    };

    const taxInvoiceColumn = {
      label: "Tax Invoice",
      field: "taxInvoice",
    };
    if (hasProviderColumn) {
      updatedColumns.splice(
        updatedColumns.length - 1,
        0,
        paymentProviderColumn
      );
    }
    if (Number(selectedBusinessVerticalId) === Number(KH_VERTICAL_ID)) {
      updatedColumns.splice(updatedColumns.length - 1, 0, taxInvoiceColumn);
    }
    return updatedColumns;
  };
  const paymentDetailsRows = selectedOrder
    ? getFormattedPaymentDetails(
        selectedOrder,
        fetchPaymentReceipt,
        isFetchPaymentReceiptLoading,
        openSnackbar,
        setOpenDialogContent
      )
    : [];

  const refundDetailsRows = selectedOrder
    ? getFormattedRefundDetails(selectedOrder.refund_details)
    : [];

  const shouldShowAccordion = selectedOrder?.payment_details?.some(
    (paymentDetail) => hasPaymentAttemptDetails(paymentDetail)
  );

  const customerDetails =
    selectedOrder && selectedOrder.address_details
      ? selectedOrder.address_details
      : null;

  const hasPaymentHead = packageDetails.some((detail) => detail.hasPaymentHead);

  const partPaymentDetails =
    selectedOrder && selectedOrder.part_payment_details
      ? getFormattedPartPaymentDetails(
          selectedOrder.part_payment_details,
          fetchPartPaymentInstallmentTaxInvoice,
          isFetchInstallmentTaxInvoiceLoading,
          openSnackbar
        )
      : [];

  const isLearnWithCancelledLoan =
    selectedOrder?.business_unit_id === LEARN_VERTICAL_ID &&
    selectedOrder?.payment_details?.some(
      (p) =>
        p.method?.id === LOAN_PAYMENT_METHOD &&
        p.status?.id === PAYMENT_STATUSES.REJECTED_BY_INFINITY_LEARN
    );
  const shouldShowPartPayment =
    partPaymentDetails.length > 0 && !isLearnWithCancelledLoan;

  return (
    <div className={styles.customerOrderDetailsContainer}>
      <div className={styles.ordersListAndCourseEnrollContainer}>
        <div className={styles.ordersList}>
          <div className={styles.contentTitle}>Customer Orders</div>
          {orderDetails
            ?.sort(
              (o1, o2) =>
                new Date(o2.order_created_date) -
                new Date(o1.order_created_date)
            )
            .map((order) => {
              const isSelected = selectedOrder?.order_id === order.order_id;
              return (
                <div
                  className={`${styles.orderBox} ${
                    isSelected ? styles.selectedOrder : ""
                  }`}
                  key={order.order_id}
                  onClick={() => {
                    if (!isSelected) {
                      setSelectedOrderID(order.order_id);
                    }
                  }}
                  style={{ cursor: isSelected ? "default" : "pointer" }}
                >
                  <DetailDisplay
                    title="Order Amount"
                    value={INR_SYMBOL + order.amount.toLocaleString("en-IN")}
                  />
                  <DetailDisplay
                    title="Punch Date"
                    value={new Date(
                      order.order_created_date
                    ).toLocaleDateString()}
                  />
                  <DetailDisplay
                    title="Status"
                    value={
                      order.status.name === "FULFILLED"
                        ? "FULLY_PAID"
                        : order.status.name
                    }
                  />
                </div>
              );
            })}
        </div>
        {shouldSmartBatchingComponent(selectedOrder) && (
          <SmartBatching uam_id={uamId} />
        )}
      </div>
      <div className={styles.customerOrderDetails}>
        <div className={styles.buttonsAndTitleContainer}>
          <div className={styles.contentTitle}>Order Details</div>
          <div className={styles.buttonContainer}>
            {" "}
            {orderStatus === ORDER_STATUSES.ACCEPTED &&
              canDiscardOrder(selectedOrder) && (
                <ErrorButton
                  onClick={handleDialogOpen(
                    setOpenDialogContent,
                    <DiscardOrder
                      onClose={handleDialogClose(setOpenDialogContent)}
                      orderId={selectedOrder.order_id}
                      setSelectedOrderID={setSelectedOrderID}
                    />,
                    { width: "25rem", height: "10rem" }
                  )}
                >
                  Discard Order
                </ErrorButton>
              )}
            {orderStatus === ORDER_STATUSES.ACCEPTED &&
              canRequestOrderCancellation(selectedOrder) && (
                <ErrorButton
                  onClick={handleDialogOpen(
                    setOpenDialogContent,
                    <CancelOrder
                      orderId={selectedOrder.order_id}
                      onClose={handleDialogClose(setOpenDialogContent)}
                    />,
                    { width: "25rem", height: "14rem" }
                  )}
                >
                  Request Cancel Order
                </ErrorButton>
              )}
            <PrimaryButton
              disabled={isOrderSummaryPDFLoading}
              onClick={() =>
                handleClickGetOrderSummaryPDFButton(
                  selectedOrder.order_id,
                  refetchOrderSummaryPDF,
                  openSnackbar
                )
              }
            >
              Get Order Summary
            </PrimaryButton>
            {orderStatus === ORDER_STATUSES.FULFILLED && !hasPaymentHead && (
              <PrimaryButton
                disabled={isTaxInvoicePDFLoading}
                onClick={() =>
                  handleClickGetTaxInvoiceButton(
                    selectedOrder.order_id,
                    fetchTaxInvoicePDF,
                    openSnackbar
                  )
                }
              >
                Get Tax Invoice
              </PrimaryButton>
            )}
            {selectedOrder &&
              canRequestTransferOrder(orderDetails, selectedOrder) && (
                <PrimaryButton
                  onClick={() =>
                    handleClickTransferOrder(
                      navigate,
                      customerUAMId,
                      selectedOrder,
                      selectedBusinessVerticalId
                    )
                  }
                >
                  Request Modification
                </PrimaryButton>
              )}
          </div>
        </div>
        <div className={styles.customerDetails}>
          {selectedOrder &&
            selectedOrder.business_unit_id === BUSINESS_VERTICALS.KH &&
            selectedOrder.application_number && (
              <div>
                <span className={styles.title}>Application No.:</span>
                <span className={styles.value}>
                  {selectedOrder.application_number}
                </span>
              </div>
            )}
          <div>
            <span className={styles.title}>Billing Name:</span>
            <span className={styles.value}>
              {customerDetails?.billing_address.recipient_name}
            </span>
          </div>
          <div>
            <span className={styles.title}>Billing Phone No:</span>
            <span className={styles.value}>
              {customerDetails?.billing_address.masked_phone_no}
            </span>
          </div>
          <div>
            <span className={styles.title}>City:</span>
            <span className={styles.value}>
              {customerDetails?.billing_address.city}
            </span>
          </div>

          <div>
            <span className={styles.title}>State:</span>
            <span className={styles.value}>
              {customerDetails?.billing_address.state}
            </span>
          </div>
          <div>
            <span className={styles.title}>Country:</span>
            <span className={styles.value}>{DEFAULT_COUNTRY_NAME}</span>
          </div>
        </div>
        <div className={styles.packageDetail}>
          <Table columns={PACKAGE_DETAILS_COLUMN} rows={packageDetails} />
          {selectedOrder && (
            <div className={styles.amountDetails}>
              <div className={styles.detailItem}>
                <span className={styles.title}>Total MRP of Packages:</span>
                <span>
                  {INR_SYMBOL +
                    selectedOrder.listed_order_amount.toLocaleString("en-IN")}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.title}>Discount Amount:</span>
                <span>
                  {INR_SYMBOL +
                    selectedOrder.total_inr_discount_amount.toLocaleString(
                      "en-IN"
                    )}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.title}>Order Amount:</span>
                <span>
                  {INR_SYMBOL + selectedOrder.amount.toLocaleString("en-IN")}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.title}>Total Paid:</span>
                <span>
                  {INR_SYMBOL +
                    selectedOrder.amount_paid.toLocaleString("en-IN")}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.title}>Total Pending:</span>
                <span>
                  {INR_SYMBOL +
                    (
                      selectedOrder.amount - selectedOrder.amount_paid
                    ).toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          )}
          <div className={styles.noteContainer}>
            {selectedOrder?.note && (
              <div className={styles.displayNoteContainer}>
                <div className={styles.noteIconAndTitle}>
                  <EventNoteIcon />
                  <Text>Note</Text>
                </div>
                <div className={styles.note}>{selectedOrder.note}</div>
              </div>
            )}
          </div>
        </div>
        <div className={styles.amountDetailsTable}>
          <div className={styles.buttonsAndTitleContainer}>
            <div className={styles.contentTitle}>Payment Details</div>
            <div className={styles.paymentButtons}>
              {!IS_JODO_DISABLED && shouldShowPartPayment ? (
                <SecondaryButton
                  onClick={() =>
                    handleOnClickEditPaymentsButton(
                      selectedOrder.order_id,
                      navigate,
                      selectedBusinessVerticalId
                    )
                  }
                >
                  View/Edit Payment Flow Type
                </SecondaryButton>
              ) : null}
              {selectedOrder && selectedOrder.payment_details.length > 0 && (
                <SecondaryButton
                  onClick={() => handleClickEditPaymentsButton(selectedOrder)}
                >
                  Edit Payments
                </SecondaryButton>
              )}
              {orderStatus === ORDER_STATUSES.ACCEPTED && (
                <PrimaryButton
                  onClick={() =>
                    handleClickProceedToPaymentButton(selectedOrder.order_id)
                  }
                >
                  Proceed to Collect Payment
                </PrimaryButton>
              )}
            </div>
          </div>
          {paymentDetailsRows.length > 0 ? (
            <div className={styles.tableContainer}>
              <Table
                columns={getPaymentDetailsColumns()}
                rows={paymentDetailsRows}
                accordion={shouldShowAccordion}
                accordionTableColumns={PAYMENTS_ATTEMPTS_COLUMN}
                maxHeight="13rem"
              />
            </div>
          ) : (
            <ErrorMessage>No payments done.</ErrorMessage>
          )}
        </div>
        {refundDetailsRows.length > 0 ? (
          <div className={styles.tableContainer}>
            <div className={styles.contentTitle}>Refund Details</div>
            <Table
              columns={REFUND_DETAILS_COLUMN}
              rows={refundDetailsRows}
              maxHeight="10rem"
            />
          </div>
        ) : null}
        {shouldShowPartPayment ? (
          <div className={styles.tableContainer}>
            <div className={styles.contentTitle}>Part Payment Details</div>
            <Table
              columns={PART_PAYMENT_DETAILS_COLUMN}
              rows={partPaymentDetails}
              maxHeight="10rem"
            />
          </div>
        ) : null}
      </div>
      <Dialog
        open={openDialogContent !== null}
        handleClose={handleDialogClose(setOpenDialogContent)}
        width={openDialogContent?.size?.width}
        height={openDialogContent?.size?.height}
      >
        {openDialogContent?.content}
      </Dialog>
    </div>
  );
};
export default CustomerOrderDetails;
